/**
 * Platform Admin → Email Sender Settings tab.
 *
 * Renders BOTH:
 *   1. The full default-sender configuration form (provider dropdown,
 *      credentials, from name/email/reply-to, Send Test Email).
 *   2. A workspace overview table showing every workspace on the
 *      platform, whether it's using the default or an override, and the
 *      from-email that will actually go out for each.
 *
 * The workspace overview lives on the same tab so a super admin can see
 * at a glance which tenants inherit the platform default and which ones
 * carry a custom configuration.
 */
import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Card, CardHeader, CardTitle, CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import PasswordInput from "@/components/ui/password-input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  EnvelopeSimple, CheckCircle, PaperPlaneTilt, WarningCircle,
  ArrowClockwise,
} from "@phosphor-icons/react";

const EMPTY = {
  provider: "resend",
  from_name: "",
  from_email: "",
  reply_to: "",
  api_key: "",
  smtp_host: "",
  smtp_port: 587,
  smtp_username: "",
  smtp_password: "",
  smtp_use_tls: true,
};

export default function PlatformEmailSenderTab() {
  const { user } = useAuth();
  const [row, setRow] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [overview, setOverview] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/platform/email-sender");
      setRow(data);
      setForm({
        provider: data.provider || "resend",
        from_name: data.from_name || "",
        from_email: data.from_email || "",
        reply_to: data.reply_to || "",
        api_key: "",
        smtp_host: data.smtp_host || "",
        smtp_port: data.smtp_port || 587,
        smtp_username: data.smtp_username || "",
        smtp_password: "",
        smtp_use_tls: data.smtp_use_tls !== false,
      });
      setTestResult(data.last_test_at ? {
        ok: !!data.last_test_ok,
        at: data.last_test_at,
        error: data.last_test_error,
      } : null);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  const loadOverview = useCallback(async () => {
    try {
      const { data } = await api.get("/platform/email-sender/workspaces-overview");
      setOverview(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => {
    load();
    loadOverview();
  }, [load, loadOverview]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.from_email.trim()) return toast.error("From email is required");
    if (form.provider === "resend" && !row?.api_key_set && !form.api_key.trim()) {
      return toast.error("Enter a Resend API key");
    }
    if (form.provider === "smtp") {
      if (!form.smtp_host.trim()) return toast.error("SMTP host is required");
      if (!form.smtp_username.trim()) return toast.error("SMTP username is required");
      if (!row?.smtp_password_set && !form.smtp_password) {
        return toast.error("SMTP password is required");
      }
    }
    setBusy(true);
    try {
      await api.put("/platform/email-sender", form);
      toast.success("Platform default saved. Every workspace on 'Use default' will now inherit these settings.");
      await load();
      await loadOverview();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const runTest = async () => {
    if (!row?.from_email) {
      return toast.error("Save the platform sender settings first, then send the test.");
    }
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/platform/email-sender/test", {});
      setTestResult({
        ok: !!data.ok,
        error: data.error,
        at: new Date().toISOString(),
        to: data.to,
      });
      if (data.ok) toast.success(`Test email sent to ${data.to}`);
      else toast.error("Test failed — see details below");
      await load();
    } catch (e) {
      const err = formatApiError(e);
      setTestResult({ ok: false, error: err, at: new Date().toISOString() });
      toast.error(err);
    } finally { setTesting(false); }
  };

  const isSmtp = form.provider === "smtp";
  const isResend = form.provider === "resend";

  const overrideCount = (overview?.workspaces || []).filter(w => !w.uses_default).length;
  const defaultCount = (overview?.workspaces || []).filter(w => w.uses_default).length;

  return (
    <div className="space-y-4" data-testid="platform-email-sender-tab">
      {/* ---------------- Configuration card ---------------- */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-primary/10 p-2"><EnvelopeSimple size={20} className="text-primary" /></div>
            <div>
              <CardTitle>Platform default email sender</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Powers outbound email for every workspace that has &quot;Use default settings&quot; enabled.
                Individual workspaces may override in their own Settings → Email sender tab.
              </p>
            </div>
            {row?.verified && (
              <Badge className="ml-auto bg-emerald-100 text-emerald-800 border-emerald-200 gap-1" data-testid="platform-sender-verified">
                <CheckCircle size={12} weight="fill" /> Working
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label>Email service provider *</Label>
            <Select value={form.provider} onValueChange={(v) => update("provider", v)}>
              <SelectTrigger data-testid="platform-sender-provider"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="resend" data-testid="platform-sender-provider-resend">Resend (recommended)</SelectItem>
                <SelectItem value="smtp" data-testid="platform-sender-provider-smtp">SMTP (Gmail, Zoho, custom server)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Sender name (from name)</Label>
              <Input
                value={form.from_name}
                onChange={e => update("from_name", e.target.value)}
                placeholder="TherapyFlow Platform"
                data-testid="platform-sender-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Sender email (from address) *</Label>
              <Input
                type="email"
                value={form.from_email}
                onChange={e => update("from_email", e.target.value)}
                placeholder="noreply@therapyflow.app"
                data-testid="platform-sender-email"
              />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label>Reply-to email (optional)</Label>
              <Input
                type="email"
                value={form.reply_to}
                onChange={e => update("reply_to", e.target.value)}
                placeholder="support@therapyflow.app"
                data-testid="platform-sender-reply-to"
              />
            </div>
          </div>

          {isResend && (
            <div className="space-y-1.5 rounded-md border bg-muted/30 p-3">
              <Label>Resend API key {row?.api_key_set ? <span className="text-emerald-700 font-normal text-[11px]">(saved · {row.api_key_hint})</span> : "*"}</Label>
              <PasswordInput
                value={form.api_key}
                onChange={e => update("api_key", e.target.value)}
                placeholder={row?.api_key_set ? "Leave blank to keep existing key" : "re_••••••••••••"}
                data-testid="platform-sender-api-key"
                autoComplete="off"
              />
              <p className="text-[11px] text-muted-foreground">
                Generate one at{" "}
                <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="text-primary hover:underline">
                  resend.com/api-keys
                </a>. Make sure the from-email domain is verified in Resend first.
              </p>
            </div>
          )}

          {isSmtp && (
            <div className="space-y-3 rounded-md border bg-muted/30 p-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-1.5 md:col-span-2">
                  <Label>SMTP host *</Label>
                  <Input
                    value={form.smtp_host}
                    onChange={e => update("smtp_host", e.target.value)}
                    placeholder="smtp.gmail.com"
                    data-testid="platform-smtp-host"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Port *</Label>
                  <Input
                    type="number"
                    value={form.smtp_port}
                    onChange={e => update("smtp_port", parseInt(e.target.value || "587", 10))}
                    placeholder="587"
                    data-testid="platform-smtp-port"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>SMTP username *</Label>
                  <Input
                    value={form.smtp_username}
                    onChange={e => update("smtp_username", e.target.value)}
                    placeholder="you@yourdomain.com"
                    data-testid="platform-smtp-username"
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label>SMTP password {row?.smtp_password_set ? <span className="text-emerald-700 font-normal text-[11px]">(saved)</span> : "*"}</Label>
                  <PasswordInput
                    value={form.smtp_password}
                    onChange={e => update("smtp_password", e.target.value)}
                    placeholder={row?.smtp_password_set ? "Leave blank to keep existing password" : "App password or SMTP secret"}
                    data-testid="platform-smtp-password"
                    autoComplete="new-password"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <input
                  id="platform-smtp-tls" type="checkbox"
                  checked={!!form.smtp_use_tls}
                  onChange={e => update("smtp_use_tls", e.target.checked)}
                  data-testid="platform-smtp-use-tls"
                />
                <label htmlFor="platform-smtp-tls" className="cursor-pointer text-muted-foreground">
                  Upgrade the connection with STARTTLS (recommended for port 587).
                </label>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2 border-t">
            <Button onClick={save} disabled={busy} data-testid="platform-sender-save">
              {busy ? "Saving…" : "Save default"}
            </Button>
            <Button
              onClick={runTest}
              disabled={testing || !row?.from_email}
              variant="outline"
              className="gap-2"
              data-testid="platform-sender-test"
            >
              <PaperPlaneTilt size={14} weight="fill" />
              {testing ? "Sending test…" : `Send test email to ${user?.email || "me"}`}
            </Button>
          </div>

          {testResult && (
            <div
              className={`rounded-md border p-3 text-sm ${testResult.ok
                ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                : "border-rose-200 bg-rose-50 text-rose-900"}`}
              data-testid={testResult.ok ? "platform-sender-test-success" : "platform-sender-test-error"}
            >
              <div className="flex items-start gap-2">
                {testResult.ok
                  ? <CheckCircle size={18} weight="fill" className="mt-0.5" />
                  : <WarningCircle size={18} weight="fill" className="mt-0.5" />
                }
                <div className="space-y-1">
                  <div className="font-medium">
                    {testResult.ok
                      ? `Test email sent successfully${testResult.to ? ` to ${testResult.to}` : ""}.`
                      : "Test send failed"}
                  </div>
                  {testResult.error && !testResult.ok && (
                    <div className="text-xs font-mono whitespace-pre-wrap break-all">
                      {testResult.error}
                    </div>
                  )}
                  {testResult.at && (
                    <div className="text-[11px] opacity-75">
                      {new Date(testResult.at).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ---------------- Workspace overview ---------------- */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <CardTitle className="text-base">Workspace overview</CardTitle>
            <span className="text-xs text-muted-foreground">
              Which workspaces inherit the platform default and which use their own configuration.
            </span>
            <Button
              variant="ghost" size="sm"
              onClick={loadOverview}
              className="ml-auto gap-1 h-7"
              data-testid="overview-refresh"
            >
              <ArrowClockwise size={12} /> Refresh
            </Button>
          </div>
          {overview && (
            <div className="flex gap-2 pt-2">
              <Badge className="bg-slate-100 text-slate-700 border-slate-200" data-testid="overview-default-count">
                {defaultCount} using default
              </Badge>
              <Badge className="bg-indigo-50 text-indigo-800 border-indigo-200" data-testid="overview-override-count">
                {overrideCount} with override
              </Badge>
            </div>
          )}
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Workplace</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>From email in effect</TableHead>
                  <TableHead>Provider</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(overview?.workspaces || []).map(w => (
                  <TableRow key={w.org_id} data-testid={`overview-row-${w.slug || w.org_id}`}>
                    <TableCell className="font-medium">
                      <div>{w.name || "—"}</div>
                      <div className="text-[10px] text-muted-foreground">/t/{w.slug}</div>
                    </TableCell>
                    <TableCell>
                      {w.uses_default ? (
                        <Badge className="bg-slate-100 text-slate-700 border-slate-200" data-testid={`overview-status-${w.slug}`}>
                          Using default
                        </Badge>
                      ) : (
                        <Badge className="bg-indigo-50 text-indigo-800 border-indigo-200 gap-1" data-testid={`overview-status-${w.slug}`}>
                          {w.override_verified && <CheckCircle size={10} weight="fill" />}
                          Custom override
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {w.from_email_in_effect || <span className="text-muted-foreground italic">not configured</span>}
                    </TableCell>
                    <TableCell className="text-xs">
                      {w.provider_in_effect === "platform_default"
                        ? <span className="text-muted-foreground">inherited</span>
                        : w.provider_in_effect}
                    </TableCell>
                  </TableRow>
                ))}
                {(!overview || overview.workspaces.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-6 text-sm text-muted-foreground">
                      No workspaces yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
